import { useAuth } from '@/state/auth';
import { Entitlement } from '@/types';
import { RoleName } from '@/utils/role.util';

const ROLE_RANK: Record<RoleName, number> = {
  readonly: 0,
  analyst: 1,
  admin: 2,
  superadmin: 3,
  financeviewer: 0,
};

export const roleHelpers = {
  isAtLeast(userRole: RoleName, requiredRole: RoleName): boolean {
    return (ROLE_RANK[userRole] ?? -1) >= (ROLE_RANK[requiredRole] ?? -1);
  },
};

/**
 * Thin wrapper over auth context that provides whoami data and entitlement checks.
 *
 * Previously a TanStack query hook — now reads from AuthState.whoami which is
 * populated on login and impersonation changes.
 *
 * @returns Object with `data`, `isLoading`, `isError`, and `hasEntitlement` helper.
 *
 * @example
 * ```tsx
 * const { data, hasEntitlement } = useWhoami();
 *
 * if (hasEntitlement('manage_accounts')) {
 *   // Show admin-only UI
 * }
 * ```
 */
export function useWhoami() {
  const auth = useAuth();
  const whoami = auth.whoami;

  /**
   * Check if the current user has a specific entitlement.
   * Returns `false` while whoami is not yet loaded (fail-safe).
   */
  function hasEntitlement(entitlement: Entitlement): boolean {
    return whoami?.entitlements?.includes(entitlement) ?? false;
  }

  return {
    data: whoami,
    isLoading: !whoami,
    isError: false,
    hasEntitlement,
  };
}
